/**
 * Owns the Top Movers strip data for the Website Rankings table: the curated
 * climbers/fallers/new-entrants lists and the loading/error flags, driven by the
 * legacy `SWReactTableWrapper` callbacks (`onDataFetch` / `getDataCallback` /
 * `onDataError`). Keeping this out of `IndustryLeadersTable` leaves that component
 * as a thin container.
 */

import { useCallback, useRef, useState } from "react";

import Sentry from "@similarweb/sentry-logger";

import type { ITopMovers } from "./websiteRankings.types";

const EMPTY_TOP_MOVERS: ITopMovers = { climbers: [], fallers: [], newEntrants: [] };

/** The subset of the transformed table response the strip consumes. */
interface ITransformedTableData {
    topMovers?: ITopMovers;
}

export const useWebsiteRankingsMomentum = () => {
    const [topMovers, setTopMovers] = useState<ITopMovers>(EMPTY_TOP_MOVERS);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isError, setIsError] = useState<boolean>(false);
    // Movers represent the full dataset, not the current page — only the initial fetch
    // should toggle the strip's loading state; pagination fetches must not flash it.
    const hasLoadedOnce = useRef(false);

    const onDataFetch = useCallback(() => {
        if (!hasLoadedOnce.current) {
            setIsLoading(true);
        }
        setIsError(false);
    }, []);

    const getDataCallback = useCallback((data: ITransformedTableData) => {
        const movers = data?.topMovers;
        if (movers?.climbers.length || movers?.fallers.length || movers?.newEntrants.length) {
            setTopMovers(movers);
        }
        hasLoadedOnce.current = true;
        setIsLoading(false);
        setIsError(false);
    }, []);

    const onDataError = useCallback((error: unknown) => {
        hasLoadedOnce.current = true;
        setIsError(true);
        setIsLoading(false);
        Sentry.error("ERROR fetching table data", error);
    }, []);

    return {
        topMovers,
        isLoading,
        isError,
        onDataFetch,
        getDataCallback,
        onDataError,
    };
};
